<template>
  <div
    :class="$style.grid"
    data-test-id="HomeBirdsEyeWidget__grid"
  >
    <GridAuto minWidth="12rem">
      <div
        v-for="item in metrics"
        :key="item.label"
      >
        <CountVertical
          v-show="item.condition"
          :count="item.total_count"
          countSize="large"
          :label="item.label"
        >
          <template v-slot:label>
            <ButtonLink
              :class="$style.countLabel"
              :text="item.label"
              @click="handleRedirect(item.label, item.url)"
            />
          </template>
        </CountVertical>
      </div>
    </GridAuto>
  </div>
</template>
<script>
import {
  ButtonLink,
  CountVertical,
  GridAuto,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModuleMap,
} from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import PolicyApi from 'client/api/v2/PolicyApi';
import SegmentEventMixin from 'client/HomePageV2/mixins/SegmentEventMixin';
import SoftwareAppsApi from 'client/api/v2/SoftwareAppsApi';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import Util from 'client/util/Util';

const organizationsCollection = StoreModuleMap[ItemTypeConsts.organizations];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const userDisplayPreferencesModule = StoreModuleMap[ItemTypeConsts.userDisplayPreferences];

const routes = {
  systemUser: ItemTypeRoutes[ItemTypeConsts.systemUser],
  userGroup: ItemTypeRoutes[ItemTypeConsts.userGroup],
  softwareApps: ItemTypeRoutes[ItemTypeConsts.softwareApps],
  deviceList: ItemTypeRoutes[ItemTypeConsts.deviceList],
  configuration: ItemTypeRoutes[ItemTypeConsts.configuration],
};

export default {
  name: 'HomeBirdsEyeWidget',

  routes,

  components: {
    ButtonLink,
    CountVertical,
    GridAuto,
  },

  mixins: [SegmentEventMixin],

  data: () => ({
    hasError: false,
    isLoading: true,
    userGroupCount: 0,
    totalPolicies: 0,
    totalApplicationCount: 0,
  }),

  computed: {
    ...mapState(userDisplayPreferencesModule, {
      displaySettings: state => state.displayPreferencesData,
    }),
    ...mapState(settingsModule, ['orgId']),

    ...mapGetters(organizationsCollection, ['getItemDetails']),

    currentUsersCount() {
      return get(this.getItemDetails(this.orgId), 'systemUsersCount', 0);
    },

    metrics() {
      const metricList = [
        {
          condition: this.orgInsights.consoleStats.totalUsers.visible,
          total_count: this.currentUsersCount,
          label: 'Users',
          url: this.$options.routes.systemUser,
        },
        {
          condition: this.orgInsights.consoleStats.totalGroups.visible,
          total_count: this.userGroupCount,
          label: 'User Groups',
          url: this.$options.routes.userGroup,
        },
        {
          condition: this.orgInsights.consoleStats.totalDevices.visible,
          total_count: this.systemsCount,
          label: 'Managed Devices',
          url: this.$options.routes.deviceList,
        },
        {
          condition: this.orgInsights.consoleStats.totalApplications.visible,
          total_count: this.totalApplicationCount,
          label: 'Managed Software',
          url: this.$options.routes.softwareApps,
        },
        {
          condition: this.orgInsights.consoleStats.totalConfigurations.visible,
          total_count: this.totalPolicies,
          label: 'Device Policies',
          url: this.$options.routes.configuration,
        },
      ];
      const displayMetricList = metricList
        .filter((item) => item.condition);
      return displayMetricList;
    },

    orgInsights() {
      return this.displaySettings.display;
    },

    systemsCount() {
      return get(this.getItemDetails(this.orgId), 'systemsCount', 0);
    },
  },

  created() {
    this.fetchCollection();
    this.getDetailsForOrganization({ id: this.orgId });
    this.handleTotalUserGroups();
    this.handleTotalPolicyCount();
    this.handleTotalApplicationCount();
  },

  methods: {
    ...mapActions(organizationsCollection, ['getDetailsForOrganization', 'fetchCollection']),

    handleRedirect(text, url) {
      this.triggerLinkClickedHandler(
        'HomeBirdsEye widget ButtonLink clicked',
        'HomeBirdsEye widget section',
        url,
        text,
      );
      Util.redirect(url);
    },

    async handleTotalApplicationCount() {
      const filters = ['CHOCOLATEY', 'APPLE_VPP', 'APPLE_CUSTOM'];
      filters.forEach(filter => {
        SoftwareAppsApi.getApplicationCount(filter).then(response => {
          this.totalApplicationCount += response.totalCount;
        });
      });
    },

    async handleTotalPolicyCount() {
      const response = await PolicyApi.fetchTotalPolicyCount();
      this.totalPolicies = response.totalCount;
    },

    handleTotalUserGroups() {
      return UserGroupApi.fetchTotalUserGroups()
        .then(({ totalCount }) => {
          this.userGroupCount = totalCount;
        })
        .catch(() => {
          this.hasError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
<style module>
.countLabel:not(.legacyOverride):not(.legacyOverride) {
  padding-bottom: var(--jc-spacer);
  text-align: left;
  top: 0.3rem;
}

.grid {
  padding-top: 1.5rem;
}
</style>
